import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import { ScrollBoxAttr } from '~/components/blocks';
import {
  GetLineVisitorAppointmentsDocument,
  HistoryLineVisitorAppointmentFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { lineVisitorHistoryPageInfoState } from '~/state/reception/atoms';

type Appointments = {
  loading: boolean;
  appointments: HistoryLineVisitorAppointmentFragment[];
};

export const useFetchAppointments = (
  scrollRef: React.MutableRefObject<ScrollBoxAttr | null>,
): Appointments => {
  const [{ lineVisitorId, page, perPage }, setPageInfo] = useAtom(lineVisitorHistoryPageInfoState);

  const [getLineVisitorAppointments, { data, loading }] = useLazyQuery(
    GetLineVisitorAppointmentsDocument,
  );

  const appointments = useMemo(
    () => (data ? getNode(data, 'LineVisitor')?.appointments.nodes || [] : []),
    [data],
  );

  useEffect(() => {
    if (lineVisitorId) {
      getLineVisitorAppointments({
        variables: {
          lineVisitorId,
          page,
          perPage,
        },
      });
    }
  }, [getLineVisitorAppointments, page, lineVisitorId, perPage]);

  useEffect(() => {
    if (data) {
      const totalPage = getNode(data, 'LineVisitor')?.appointments.pagesCount ?? 1;
      setPageInfo((_state) => ({ ..._state, totalPage }));

      scrollRef.current?.toTop();
    }
  }, [data, setPageInfo, scrollRef]);

  return { loading, appointments };
};
